<template>
  <section class="quote-band dark" :id="blockId || undefined">
    <div class="container">
      <div class="quote-row">
        <section-title
          align="left"
          tone="dark"
          flush
          data-aos="fade-right"
          :kicker="kicker || t('Custom quote')"
          :title="title"
          :lede="lede"
        />
        <app-button
          class="quote-cta"
          href="#cta"
          tone="dark"
          icon="paper-plane"
          data-aos="fade-left"
          @click.prevent="goCta"
        >
          {{ cta || t('Get in touch') }}
        </app-button>
      </div>

      <panel-list
        v-if="points.length"
        class="quote-points"
        layout="cards"
        tone="dark"
        :columns="pointColumns"
        :items="points"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Title rail on the left, button on the right, button centred against the
   copy. The heading is rendered flush, so the lift it would otherwise carry
   (SectionTitle's 1.75rem) is owned by this row instead: centred, the grid
   would align the heading's margin box rather than its text and the button
   would sit ~14px below centre, which is the low button this replaces.

   The lift moves to padding-bottom on one row, so the gap before
   .quote-points is 1.75 + 2.5 = 4.25rem exactly as it was; stacked there is no
   below-the-row gap to keep, so row-gap becomes the previous 2rem plus that
   handover = 3.75rem, which is the same 3.75rem the stacked heading and button
   already had between them. Nothing but the button moves. */
.quote-row {
  align-items: center;
  column-gap: 3rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  padding-bottom: 1.75rem;
  row-gap: 3.75rem;

  @media (max-width: 767.98px) {
    align-items: start;
    grid-template-columns: 1fr;
    padding-bottom: 0;
  }
}

/* The row's second column is `auto`, so the button gets its natural width and
   would otherwise wrap its label the moment that column gets tight. Stacked, the
   button has the column to itself and no longer needs to hold one line, so the
   label can wrap rather than run past the band. */
.quote-cta {
  @media (min-width: 768px) {
    white-space: nowrap;
  }
}

/* The fine print is PanelList now. It was the same translucent dark card, the
   same hairline beam, the same 2.4rem primary tile and the same lift as the
   why cards and the include panels, so the panel owns that anatomy. All that is
   left here is where the grid sits under the row. */
.quote-points {
  margin-top: 2.5rem;
}
</style>

<script>
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { scrollToId } from '@/smoothScroll';
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/Components/SectionTitle.vue';
import AppButton from '@/Components/AppButton.vue';
import PanelList from '@/Components/PanelList.vue';

FontAwesomeLibrary.add(faPaperPlane);

export default {
  components: {
    AppButton,
    PanelList,
    SectionTitle
  },

  props: {
    title: {
      type: String,
      required: true
    },
    lede: {
      type: String,
      required: true
    },
    kicker: {
      type: String,
      default: ''
    },
    cta: {
      type: String,
      default: ''
    },
    points: {
      type: Array,
      default: () => []
    },
    blockId: {
      type: String,
      default: ''
    }
  },

  computed: {
    /* The points are three short cards; a fourth would still read as three
       across. One or two keep their own count so a short list is not three
       narrow columns. */
    pointColumns() {
      return Math.min(this.points.length, 3);
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Custom quote': 'Custom quote',
        },
        'zh-MY': {
          'Custom quote': '客制报价',
        },
        'ms-MY': {
          'Custom quote': 'Sebut harga khas',
        }
      }
    });

    return { t };
  },

  methods: {
    goCta() {
      scrollToId('cta');
    }
  }
};
</script>
